import { Options, default as libNormalizeUrl } from 'normalize-url';
import { getNormalizeUrlRegexes } from '../db/urlNormalizationPrefs';
import { logger } from '../utils/logger';

export interface NormalizedUrl {
  original: string;
  normalized: string;
  domainSortKey: string;
}

// Include all options here for clarity, including defaults
const options: Options = {
  defaultProtocol: 'https', // changed
  normalizeProtocol: true,
  forceHttp: false,
  forceHttps: true, // changed
  stripAuthentication: true,
  stripHash: true, // changed
  stripProtocol: false,
  stripTextFragment: true,
  stripWWW: true,
  removeQueryParameters: [/^utm_\w+/i],
  removeTrailingSlash: true,
  removeSingleSlash: true,
  removeDirectoryIndex: false,
  removeExplicitPort: false,
  sortQueryParameters: true,
};

const sortOptions: Options = {
  defaultProtocol: 'https', // changed
  normalizeProtocol: true,
  forceHttp: false,
  forceHttps: true, // changed
  stripAuthentication: true,
  stripHash: true, // changed
  stripProtocol: true, // changed
  stripTextFragment: true,
  stripWWW: true,
  removeQueryParameters: [/^utm_\w+/i],
  removeTrailingSlash: true,
  removeSingleSlash: true,
  removeDirectoryIndex: false,
  removeExplicitPort: false,
  sortQueryParameters: true,
};

const googleDocsOptions: Options = {
  ...options,
  removeQueryParameters: true,
};

const amazonOptions: Options = {
  removeQueryParameters: [/^th/i],
};

export function makeDomainSortKey(url: string): string {
  const minusProtocol = url.slice(0, 8) === 'https://' ? url.slice(8) : null;
  const parts = minusProtocol?.split('/');
  const host = parts?.[0];
  const path = parts?.slice(1).join('/');
  const domainReversed = host?.split('.').reverse().slice(1).join('.'); // Drop TLD
  return domainReversed + '/' + path;
}

export function normalizeUrl(chromeUrl: string): NormalizedUrl {
  // console.trace('Normalizing URL', chromeUrl);

  if (chromeUrl.startsWith('todo://')) {
    return { normalized: chromeUrl, original: chromeUrl, domainSortKey: chromeUrl };
  }

  if (chromeUrl.indexOf('chrome-extension://') === 0 && chromeUrl.indexOf('uri=') != -1) {
    chromeUrl = decodeURIComponent(chromeUrl.split('uri=')[1]);
  }

  // Passthrough for non-http(s) URLs (chrome://, chrome-devtools://, etc.)
  // normalize-url is designed for http/https and produces garbage for other protocols.
  if (!chromeUrl.startsWith('http://') && !chromeUrl.startsWith('https://')) {
    return { normalized: chromeUrl, original: chromeUrl, domainSortKey: chromeUrl };
  }

  var normalized: string;
  if (chromeUrl.indexOf('docs.google.com') !== -1) {
    normalized = libNormalizeUrl(chromeUrl, googleDocsOptions);
  }
  else if (chromeUrl.indexOf('amazon.com') !== -1) {
    normalized = libNormalizeUrl(chromeUrl, amazonOptions);
  } else {
    try { 
      new URL(chromeUrl);
    } catch (e) {
      logger.error('Error parsing URL', chromeUrl, e);
      throw e;
    }
    normalized = libNormalizeUrl(chromeUrl, options);
  }

  for (const [pattern, replacement] of getNormalizeUrlRegexes()) {
    const regex = new RegExp(pattern);
    const newNormalized = normalized.replace(regex, replacement);
    normalized = newNormalized;
  }

  const domainSortKey = makeDomainSortKey(normalized);

  return { normalized: normalized, original: chromeUrl, domainSortKey: domainSortKey };
}
